import type { Option } from '@usealloy/component-library';
import type { DropdownOption } from '~/flow/types';
import { AggregationTypeWithTimeframe } from 'flow/store/engine/constants';
import type { AggregationType, TimeframeUnit } from 'flow/store/engine/constants';
import {
  AGGREGATION_TYPE_LABELS,
  NON_REFERENCEABLE_UI_DATA_TYPES,
  SCOPE_MAX_DAYS,
} from './constants';
import { AggregationFilterOperator } from './types';
import type {
  AggregationColumn,
  AggregationColumnOption,
  AggregationConfigByObjectType,
  AggregationFilterOption,
  BaseAggregationUiDataType,
  AggregationObjectType,
  AggregationSourceData,
  AggregationValidationErrors,
  OptionGroup,
} from './types';
import type { AggregationFilter, PublishedAttributeMetadata, DeviceDetailMetadata } from 'flow/store/engine/types';

/**
 * Returns the UI-facing key for the selected aggregation column.
 * For PA columns this is the token (matching option.value); for device-detail columns it's the field name;
 * for schema columns it's the column slug itself.
 */
export const getColumnKey = (
  source: Pick<AggregationFilter, 'columnSlug' | 'publishedAttributeMetadata' | 'deviceDetailMetadata'>,
): string => source.publishedAttributeMetadata?.publishedAttributeToken
  ?? source.deviceDetailMetadata?.deviceDetailField
  ?? source.columnSlug;

/**
 * Extracts publishedAttributeMetadata or deviceDetailMetadata from an aggregation
 * column/filter option, returning an object suitable for spreading into an
 * AggregationFilter or AggregationComponent's aggregation field.
 */
export function getAggregationMetadata(option: AggregationFilterOption): {
  publishedAttributeMetadata?: PublishedAttributeMetadata;
  deviceDetailMetadata?: DeviceDetailMetadata;
} {
  if (option.ui_data_type === 'published_attribute') {
    return {
      publishedAttributeMetadata: {
        publishedAttributeToken: option.value!, // Option.value is guaranteed non-null
        publishedAttributeDescription: option.published_attribute_description ?? null,
        publishedAttributeName: option.published_attribute_name,
        publishedAttributeDataType: option.data_type,
        containsPii: option.contains_pii,
      },
    };
  }
  if (option.ui_data_type === 'device_detail') {
    return {
      deviceDetailMetadata: {
        deviceDetailField: option.value!,
        uiDataType: option.device_detail_ui_data_type!,
        containsPii: option.contains_pii ?? false,
        sourceColumn: option.column_slug,
      },
    };
  }
  return {};
}

/** Resolves the effective ui_data_type, unwrapping published_attribute / device_detail to its sub-type. */
export const resolveDataType = (
  filterOption?: AggregationFilterOption,
): BaseAggregationUiDataType => {
  if (filterOption?.ui_data_type === 'published_attribute') {
    return filterOption.published_attribute_ui_data_type ?? 'string';
  }
  if (filterOption?.ui_data_type === 'device_detail') {
    return filterOption.device_detail_ui_data_type ?? 'string';
  }
  return (filterOption?.ui_data_type ?? 'string');
};

/** Sorts a copy of options alphabetically by `label` (locale-aware). */
export function sortAggregationFilterOptionsByLabel<T extends AggregationFilterOption>(
  options: T[],
): T[] {
  return [...options].sort((a, b) => String(a.label ?? '').localeCompare(String(b.label ?? '')));
}

/**
 * Partitions options into schema, published-attribute, and device-detail groups for dropdowns.
 * PA options are split by source column (`column_slug`); the group label is that column's
 * `display_name`. Group order follows first appearance in `options` (config column order).
 * Preserves input order within each group; sort first with `sortAggregationFilterOptionsByLabel`
 * when needed.
 */
export function buildOptionGroups<T extends AggregationFilterOption>({
  options,
  objectDisplayName,
}: {
  options: T[];
  objectDisplayName: string;
}): OptionGroup<T>[] {
  if (options.length === 0) return [];
  const schemaOptions: T[] = [];
  const publishedAttrGroups = new Map<string, { label: string; options: T[] }>();
  const deviceDetailOptions: T[] = [];

  for (const option of options) {
    if (option.ui_data_type === 'published_attribute') {
      const existing = publishedAttrGroups.get(option.column_slug);
      if (existing) {
        existing.options.push(option);
      } else {
        publishedAttrGroups.set(option.column_slug, {
          label: option.display_name,
          options: [option],
        });
      }
    } else if (option.ui_data_type === 'device_detail') {
      deviceDetailOptions.push(option);
    } else {
      schemaOptions.push(option);
    }
  }

  const groups: OptionGroup<T>[] = [];

  if (schemaOptions.length > 0) {
    groups.push({
      label: `${objectDisplayName} Schema`,
      value: null,
      options: schemaOptions,
    });
  }

  for (const { label, options: paOptions } of publishedAttrGroups.values()) {
    groups.push({ label, value: null, options: paOptions });
  }

  if (deviceDetailOptions.length > 0) {
    groups.push({
      label: `${objectDisplayName} Device Details`,
      value: null,
      options: deviceDetailOptions,
    });
  }

  return groups;
}

function findColumnByKey(
  columnOptions: AggregationColumnOption[],
  key: string,
): AggregationColumnOption | undefined {
  return columnOptions.find(col => col.value === key);
}

/**
 * Derives the aggregation-type dropdown options from the selected column.
 * Returns an empty array when no column is selected or the column has no
 * allowed aggregation types.
 */
export function getAggregationTypeOptions(
  columnOptions: AggregationColumnOption[],
  selectedColumnKey: string,
): DropdownOption[] {
  const selected = findColumnByKey(columnOptions, selectedColumnKey);
  if (!selected) return [];
  return selected.aggregations
    .filter(type => type in AGGREGATION_TYPE_LABELS)
    .map(type => ({
      label: AGGREGATION_TYPE_LABELS[type],
      value: type,
    }));
}

/**
 * Returns the current aggregationType if it is still valid for the new column,
 * otherwise falls back to the first allowed type for that column.
 */
export function reconcileAggregationType(
  columnOptions: AggregationColumnOption[],
  newColumnKey: string,
  currentAggregationType: AggregationType,
): AggregationType {
  const col = findColumnByKey(columnOptions, newColumnKey);
  if (!col) return currentAggregationType;
  const supported = col.aggregations.filter(type => type in AGGREGATION_TYPE_LABELS);
  if (!supported.length) return currentAggregationType;
  return supported.includes(currentAggregationType)
    ? currentAggregationType
    : supported[0];
}

/**
 * Whether the aggregation editor should show the timeframe row.
 *
 * Entity aggregations for AVG/MAX/MIN/SUM read the entity's latest
 * denormalized value, so a timeframe is meaningless there and is replaced with
 * a static "Entity's Latest Value" clause. Every other object type (e.g.
 * transaction, login) aggregates over its own rows, so a timeframe always
 * applies regardless of aggregation type.
 */
export function getShowTimeframeFilter(
  aggregationType: AggregationType,
  objectType: AggregationObjectType | undefined,
): boolean {
  if (objectType !== 'entity') return true;
  return (Object.values(AggregationTypeWithTimeframe) as string[]).includes(aggregationType);
}

function expandColumnOptions(column: AggregationColumn): AggregationColumnOption[] {
  switch (column.ui_data_type) {
    case 'published_attribute': {
      if (!column.published_attribute_metadata?.published_attributes) return [];
      return column.published_attribute_metadata.published_attributes.map(pa => ({
        ui_data_type: column.ui_data_type,
        column_slug: column.column_slug,
        display_name: column.display_name,
        value: pa.published_attribute_token,
        label: pa.published_attribute_name
          || pa.published_attribute_evaluation_payload_path,
        published_attribute_description: pa.published_attribute_description,
        published_attribute_ui_data_type: pa.published_attribute_ui_data_type,
        published_attribute_name: pa.published_attribute_name,
        data_type: pa.data_type,
        contains_pii: pa.contains_pii,
        aggregations: pa.aggregations,
        filter_operators: pa.filter_operators,
        possible_values: pa.possible_values,
      }));
    }
    case 'device_detail': {
      if (!column.device_detail_metadata?.device_detail_fields) return [];
      return column.device_detail_metadata.device_detail_fields.map(field => ({
        ui_data_type: column.ui_data_type,
        column_slug: column.column_slug,
        display_name: column.display_name,
        value: field.device_detail_field,
        label: field.display_name,
        device_detail_ui_data_type: field.ui_data_type,
        contains_pii: field.contains_pii,
        aggregations: field.aggregations,
        filter_operators: field.filter_operators,
        possible_values: field.possible_values,
      }));
    }
    default:
      return [{
        label: column.display_name,
        value: column.column_slug,
        column_slug: column.column_slug,
        display_name: column.display_name,
        aggregations: column.aggregations,
        ui_data_type: column.ui_data_type,
        filter_operators: column.filter_operators,
        possible_values: column.possible_values,
        wrapped_encryption_key_title: column.wrapped_encryption_key_title,
      }];
  }
}

export function toColumnOptions(
  columns: AggregationColumn[],
): AggregationColumnOption[] {
  return columns
    .filter(col => col.user_aggregable)
    .flatMap(expandColumnOptions)
    .filter(option => option.aggregations.some(
      type => type in AGGREGATION_TYPE_LABELS,
    ));
}

export function toFilterOptions(
  columns: AggregationColumn[],
): AggregationFilterOption[] {
  return columns
    .filter(col => col.user_filterable)
    .flatMap(expandColumnOptions)
    .filter(option => option.filter_operators?.length);
}

const FILTER_OPERATOR_OPTIONS: Option[] = [
  { value: AggregationFilterOperator.EQ, label: '=' },
  { value: AggregationFilterOperator.NE, label: '!=' },
  { value: AggregationFilterOperator.LT, label: '<' },
  { value: AggregationFilterOperator.LTE, label: '<=' },
  { value: AggregationFilterOperator.GT, label: '>' },
  { value: AggregationFilterOperator.GTE, label: '>=' },
  { value: AggregationFilterOperator.IN, label: 'in' },
  { value: AggregationFilterOperator.BT, label: 'between' },
];

/**
 * Returns operator dropdown options for a filter, scoped to the
 * column's allowed operators when available. When `currentOperatorSlug`
 * is provided, guarantees it appears in the result with its display label.
 */
export function getOperatorOptionsForFilter(
  filterOption?: AggregationFilterOption,
  currentOperatorSlug?: string,
): Option[] {
  if (!filterOption) {
    const current = FILTER_OPERATOR_OPTIONS.find(opt => opt.value === currentOperatorSlug);
    return current ? [current] : [];
  }
  const allowed = new Set(filterOption.filter_operators);
  const options = FILTER_OPERATOR_OPTIONS.filter(
    opt => opt.value && allowed.has(opt.value),
  );
  if (!currentOperatorSlug || options.some(opt => opt.value === currentOperatorSlug)) {
    return options;
  }
  const fallback = FILTER_OPERATOR_OPTIONS.find(opt => opt.value === currentOperatorSlug);
  return fallback ? [...options, fallback] : options;
}

/**
 * Columns a dynamic filter may name as its source, including the filtered column
 * itself since that is the default selection. Only pairings that share a
 * `ui_data_type` and a `wrapped_encryption_key_title` are offered — anything else
 * would compare a hash to plaintext and silently match nothing. Published-attribute
 * and device-detail columns cannot be a source, so they get an empty list.
 */
export function getReferenceableFieldOptions({
  columns,
  columnSlug,
}: {
  columns: AggregationColumn[];
  columnSlug: string;
}): AggregationFilterOption[] {
  const filteredColumn = columns.find(col => col.column_slug === columnSlug);

  if (
    !filteredColumn
    || NON_REFERENCEABLE_UI_DATA_TYPES.has(filteredColumn.ui_data_type)
  ) {
    return [];
  }

  const compatible = columns.filter(col => col.user_filterable
    && col.ui_data_type === filteredColumn.ui_data_type
    && (col.wrapped_encryption_key_title ?? null)
      === (filteredColumn.wrapped_encryption_key_title ?? null));

  return sortAggregationFilterOptionsByLabel(
    compatible.flatMap(expandColumnOptions),
  );
}

function isMultiStaticValue(
  staticValue: AggregationFilter['staticValue'],
): staticValue is string[] {
  return Array.isArray(staticValue);
}

function isScalarStaticValue(
  staticValue: AggregationFilter['staticValue'],
): staticValue is string {
  return typeof staticValue === 'string';
}

function hasStaticValue(filter: AggregationFilter): boolean {
  const { staticValue } = filter;
  if (staticValue == null) return false;
  if (isScalarStaticValue(staticValue)) return staticValue !== '';
  if (isMultiStaticValue(staticValue)) return staticValue.length > 0;
  // Remaining type is AggregationFilterTimeframeValue, which is always a set object.
  return true;
}

export function hasIncompleteFilters(filters: AggregationFilter[]): boolean {
  return filters.some(filter => !filter.dynamicColumnSlug && !hasStaticValue(filter));
}

const UNIT_TO_DAYS_FACTOR: Record<string, number> = {
  minutes: 1 / 1440,
  hours: 1 / 24,
  days: 1,
  months: 30,
};

export function convertToDays(value: number, unit: TimeframeUnit): number {
  return value * (UNIT_TO_DAYS_FACTOR[unit] ?? 1);
}

export function getTimeframeMaxExceededMessage(
  scope: string,
  unit: TimeframeUnit,
  intervalEnd: number,
): string | null {
  const maxDays = SCOPE_MAX_DAYS[scope as keyof typeof SCOPE_MAX_DAYS];
  if (maxDays == null) return null;
  const valueInDays = convertToDays(intervalEnd, unit);
  if (valueInDays <= maxDays) return null;
  return `The maximum timeframe for this scope is ${maxDays} days (or equivalent).`;
}

export function getValidationErrors(
  sourceData: AggregationSourceData,
): AggregationValidationErrors {
  const errors: AggregationValidationErrors = {};
  if (!sourceData.name) errors.name = 'Name is required';
  if (!sourceData.description?.trim()) errors.description = 'Description is required';
  return errors;
}

/**
 * Returns true when the aggregation editor should prevent saving.
 * Checks non-validation fields: aggregation column/type, scope, timeframe.
 * Name/description are handled by getValidationErrors; possible_values
 * membership is validated in handleDone (AggregationsSubEditorContainer).
 */
export function getIsSaveDisabled(
  sourceData: AggregationSourceData,
): boolean {
  const {
    aggregation: { columnSlug, aggregationType },
    scope,
    timeframeUnit,
    timeframeIntervalEnd,
    timeframeIntervalStart,
  } = sourceData;

  const hasEmptyBaseField = !columnSlug
    || !aggregationType
    || !scope
    || !timeframeUnit
    || timeframeIntervalEnd == null
    || timeframeIntervalStart == null;

  return hasEmptyBaseField;
}

// --- API config response formatting ---

type ClientQueryDataConfigObjectVersion = {
  active_selects: boolean;
  public_version: number;
  columns: AggregationColumn[];
};

type ClientQueryDataConfigGetObjectsResponse = {
  data_objects: {
    object_display_name: string;
    object_slug: AggregationObjectType;
    use_cases: string[];
    versions: ClientQueryDataConfigObjectVersion[];
  }[];
};

export function getFormattedAggregationConfig(
  clientQueryDataConfigObjects:
  ClientQueryDataConfigGetObjectsResponse['data_objects'],
): AggregationConfigByObjectType {
  return clientQueryDataConfigObjects.reduce(
    (acc, configObject) => {
      if (!configObject.use_cases.includes('aggregations')) {
        return acc;
      }

      const activeVersion = configObject.versions.find(
        version => version.active_selects,
      );

      if (!activeVersion) {
        return acc;
      }

      const { object_slug: objectSlug } = configObject;

      acc[objectSlug] = {
        columns: activeVersion.columns,
        objectDisplayName: configObject.object_display_name,
        objectType: objectSlug,
        publicVersion: activeVersion.public_version,
      };

      return acc;
    },
    {} as AggregationConfigByObjectType,
  );
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function roundToDecimalPlaces(n: number, decimalPlaces: number): number {
  const factor = 10 ** decimalPlaces;
  return Math.round(n * factor) / factor;
}

export function parseAndClamp(args: {
  rawValue: string;
  clampValues: { min: number; max: number };
  decimalScale: number;
}): number {
  const { rawValue, clampValues: { min, max }, decimalScale } = args;
  const isDecimal = decimalScale > 0;

  if (rawValue === '' || rawValue === '-') {
    return min;
  }

  const parsed = isDecimal
    ? Number.parseFloat(rawValue)
    : Number.parseInt(rawValue, 10);

  if (Number.isNaN(parsed)) {
    return min;
  }

  const normalized = isDecimal
    ? roundToDecimalPlaces(parsed, decimalScale)
    : Math.trunc(parsed);

  return clamp(normalized, min, max);
}
